import {
  doc,
  collection,
  serverTimestamp,
  writeBatch,
  getDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../lib/firebase";
import type {
  Audience,
  Session,
  SessionCreate,
  DeleteSessionInput,
  DeleteSessionResponse,
  BulkCreateSessionsInput,
  BulkCreateSessionsResponse,
  BulkResolveSessionsInput,
  BulkResolveSessionsResponse,
} from "@event-planner/shared";
import {
  resolveAudienceTarget,
  isAnonymousAudience,
} from "./audienceService";

const deleteSessionCallable = httpsCallable<
  DeleteSessionInput,
  DeleteSessionResponse
>(functions, "deleteSession");

const bulkCreateSessionsCallable = httpsCallable<
  BulkCreateSessionsInput,
  BulkCreateSessionsResponse
>(functions, "bulkCreateSessions");

const bulkResolveSessionsCallable = httpsCallable<
  BulkResolveSessionsInput,
  BulkResolveSessionsResponse
>(functions, "bulkResolveSessions");

export async function bulkResolveSessions(
  input: BulkResolveSessionsInput,
): Promise<BulkResolveSessionsResponse> {
  const result = await bulkResolveSessionsCallable(input);
  return result.data;
}

/**
 * Shape the admin SessionForm passes to `createSession` (ADR-120). The
 * form passes the picked audience IDs (named audiences plus personal
 * audience IDs derived from picked emails); the service resolves them
 * into either a single reused audience or an anonymous composition,
 * then writes everything in one Firestore writeBatch.
 *
 * CF-managed fields (`date`, `targetedPersonIds`, `speakerNames`,
 * `locationLabel`) are excluded by `SessionCreate` and placeholdered
 * server-side. `speakerIds` is client-writable and carried through on
 * create (plain create passes `[]`; duplicate passes the source's
 * speakers) — `onSessionWrite` derives `speakerNames` from it.
 *
 * `audienceId` itself is excluded from the form input and computed by
 * the service from `pickedAudienceIds`.
 */
export type CreateSessionInput = Omit<SessionCreate, "audienceId"> & {
  /**
   * Audience IDs picked by the form. Includes named audiences (custom
   * groups + role audiences) directly, plus personal audience IDs
   * derived from picked emails by the form layer
   * (`personalAudienceId(eventId, uid)`).
   */
  pickedAudienceIds: string[];
};

export type UpdateSessionInput = Partial<Omit<CreateSessionInput, "eventId">>;

/**
 * Create a session. Single batched write covers:
 *
 *   - the session doc
 *   - an anonymous composition audience iff the picker resolved to >1
 *     audience (multiple groups, or audience + people, etc.)
 *
 * Atomic: either both writes commit or neither does.
 */
export async function createSession(
  input: CreateSessionInput,
): Promise<string> {
  const { eventId, pickedAudienceIds, ...rest } = input;
  const target = resolveAudienceTarget(eventId, pickedAudienceIds);

  const sessionRef = doc(collection(db, "sessions"));
  const batch = writeBatch(db);

  const sessionDoc = {
    ...rest,
    eventId,
    audienceId:
      target.mode === "reuse" ? target.audienceId : target.audienceRef.id,
    // CF-managed placeholders. `speakerIds` is NOT one of them — it is
    // client-writable and spread from `...rest` above so duplicated
    // sessions carry their speakers (speakerNames is then derived by
    // the onSessionWrite trigger).
    date: "",
    targetedPersonIds: [],
    speakerNames: [],
    locationLabel: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  batch.set(sessionRef, sessionDoc);

  if (target.mode === "compose") {
    batch.set(target.audienceRef, target.audienceData);
  }

  await batch.commit();
  return sessionRef.id;
}

/**
 * Update a session's fields and (optionally) its audience. The flow:
 *
 *   1. Read the current session to discover its previous audienceId.
 *   2. Resolve the new picked audience IDs into reuse or compose.
 *   3. Read the previous audience to check if it's an orphan-able
 *      anonymous composition.
 *   4. Single writeBatch:
 *        - update the session
 *        - (if compose) write the new anonymous audience
 *        - (if previous was anonymous and is being abandoned) delete it
 */
export async function updateSession(
  eventId: string,
  sessionId: string,
  input: UpdateSessionInput,
): Promise<void> {
  const { pickedAudienceIds, ...rest } = input;
  const sessionRef = doc(db, "sessions", sessionId);
  const batch = writeBatch(db);

  // No audience change requested → simple update.
  if (pickedAudienceIds === undefined) {
    batch.update(sessionRef, {
      ...rest,
      updatedAt: serverTimestamp(),
    });
    await batch.commit();
    return;
  }

  const target = resolveAudienceTarget(eventId, pickedAudienceIds);

  // Read the session's previous audienceId so we can clean up an old
  // anonymous composition that's being abandoned.
  const sessionSnap = await getDoc(sessionRef);
  const prevAudienceId = sessionSnap.exists()
    ? (sessionSnap.data() as Session).audienceId
    : null;

  const newAudienceId =
    target.mode === "reuse" ? target.audienceId : target.audienceRef.id;

  batch.update(sessionRef, {
    ...rest,
    audienceId: newAudienceId,
    updatedAt: serverTimestamp(),
  });

  if (target.mode === "compose") {
    batch.set(target.audienceRef, target.audienceData);
  }

  if (prevAudienceId && prevAudienceId !== newAudienceId) {
    const prevAudSnap = await getDoc(doc(db, "audiences", prevAudienceId));
    if (prevAudSnap.exists()) {
      const prevAud = prevAudSnap.data() as Audience;
      if (isAnonymousAudience(prevAud)) {
        batch.delete(prevAudSnap.ref);
      }
    }
  }

  await batch.commit();
}

/**
 * Cascade-delete a session through the `deleteSession` callable
 * (ADR-018). Direct `deleteDoc` is blocked. The callable also cleans up
 * the session's anonymous composition audience if any.
 */
export async function deleteSession(
  eventId: string,
  sessionId: string,
): Promise<void> {
  await deleteSessionCallable({ eventId, sessionId });
}

export async function bulkCreateSessions(
  input: BulkCreateSessionsInput,
): Promise<BulkCreateSessionsResponse> {
  const result = await bulkCreateSessionsCallable(input);
  return result.data;
}
